import { useState, useEffect, useCallback } from 'react';
import {
  goalLibrarySectionsApi,
  goalLibraryTemplatesApi,
  goalLibraryGoalsApi,
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, X, Target, BookOpen, Loader2, Save, ChevronLeft, Layers } from 'lucide-react';
import SectionsPanel from '../components/goal-library/SectionsPanel';

// ---------------------------------------------------------------------------
// InlineTemplateCreateForm — creates a new top-level template (title + active)
// ---------------------------------------------------------------------------
const InlineTemplateCreateForm = ({ onSave, onCancel }) => {
  const [title, setTitle] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error('Please enter a template title');
      return;
    }
    setSaving(true);
    try {
      await onSave({ title: title.trim(), is_active: isActive });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border rounded-lg p-4 bg-white space-y-4 shadow-sm">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-sm flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-primary" />
          New Template
        </h4>
        <div className="flex items-center gap-1.5">
          <Switch
            id="new-template-active"
            checked={isActive}
            onCheckedChange={setIsActive}
          />
          <Label htmlFor="new-template-active" className="text-xs cursor-pointer">
            Active
          </Label>
        </div>
      </div>

      <div>
        <Label className="text-xs text-muted-foreground mb-1 block">Template Title</Label>
        <Input
          autoFocus
          placeholder="e.g. Sales Team Goals 2026"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') onCancel(); }}
        />
      </div>

      <div className="flex justify-end gap-2 pt-1 border-t">
        <Button variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" onClick={handleSave} disabled={saving}>
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin mr-1" />
          ) : (
            <Save className="h-4 w-4 mr-1" />
          )}
          Save Template
        </Button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// TemplateCard — card in the templates list view
// ---------------------------------------------------------------------------
const TemplateListCard = ({ template, onClick, onDelete, onToggleActive }) => {
  const [toggling, setToggling] = useState(false);

  const handleToggle = async (e) => {
    e.stopPropagation();
    setToggling(true);
    try {
      await onToggleActive(template.id, !template.is_active);
    } finally {
      setToggling(false);
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete(template);
  };

  return (
    <div
      className="p-4 rounded-lg border bg-white hover:border-primary/40 transition-colors cursor-pointer"
      onClick={onClick}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <BookOpen className="h-4 w-4 text-primary flex-shrink-0" />
          <span className="font-medium text-sm truncate">{template.title}</span>
          <Badge
            variant={template.is_active ? 'default' : 'outline'}
            className={`text-xs flex-shrink-0 ${template.is_active ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'text-slate-500'}`}
          >
            {template.is_active ? 'Active' : 'Inactive'}
          </Badge>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          <Badge variant="outline" className="text-xs">
            {template.section_count ?? 0} section{template.section_count !== 1 ? 's' : ''}
          </Badge>
          <Badge variant="outline" className="text-xs">
            {template.goal_count ?? 0} goal{template.goal_count !== 1 ? 's' : ''}
          </Badge>
          <Badge
            variant="outline"
            className={`text-xs ${
              (template.total_weight ?? 0) > 100
                ? 'border-red-300 text-red-600 bg-red-50'
                : (template.total_weight ?? 0) === 100
                ? 'border-emerald-300 text-emerald-700 bg-emerald-50'
                : 'text-slate-500'
            }`}
          >
            {template.total_weight ?? 0}% / 100%
          </Badge>
          <Badge variant="outline" className="text-xs">
            {template.assigned_user_count ?? 0} assigned
          </Badge>
          <Switch
            checked={template.is_active}
            onCheckedChange={() => {}}
            onClick={handleToggle}
            disabled={toggling}
            className="scale-75"
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDelete}
            className="h-7 w-7 p-0 text-destructive"
            title="Delete template"
          >
            {toggling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// InlineLibraryGoalForm — create/edit a goal inside a template detail view
// ---------------------------------------------------------------------------
const InlineLibraryGoalForm = ({ editingGoal, onSave, onCancel, remainingBudget = 100 }) => {
  const initKpis = editingGoal?.kpis?.length
    ? editingGoal.kpis
    : [{ name: '', measurement: '', timeline: '', weight: '' }];

  const [title, setTitle] = useState(editingGoal?.title || '');
  const [kpis, setKpis] = useState(initKpis);
  const [isActive, setIsActive] = useState(
    editingGoal?.is_active !== undefined ? editingGoal.is_active : true
  );
  const [saving, setSaving] = useState(false);

  const totalWeight = kpis.reduce((sum, k) => sum + (parseFloat(k.weight) || 0), 0);

  const addKpi = () =>
    setKpis([...kpis, { name: '', measurement: '', timeline: '', weight: '' }]);

  const removeKpi = (idx) => {
    if (kpis.length > 1) setKpis(kpis.filter((_, i) => i !== idx));
  };

  const updateKpi = (idx, field, val) => {
    const next = [...kpis];
    next[idx] = { ...next[idx], [field]: val };
    setKpis(next);
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error('Please enter a goal title');
      return;
    }
    if (kpis.length === 0) {
      toast.error('At least one KPI is required');
      return;
    }
    const hasEmpty = kpis.some((k) => !k.name.trim());
    if (hasEmpty) {
      toast.error('Please fill in KPI Name for all KPIs');
      return;
    }
    if (totalWeight > remainingBudget) {
      toast.error(`Goal weight ${totalWeight}% exceeds the remaining template budget of ${remainingBudget}%. Template total cannot exceed 100%.`);
      return;
    }

    setSaving(true);
    try {
      await onSave({
        title: title.trim(),
        kpis: kpis.map((k) => ({
          name: k.name,
          measurement: k.measurement,
          timeline: k.timeline,
          weight: parseFloat(k.weight) || 0,
        })),
        is_active: isActive,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border rounded-lg p-4 bg-white space-y-4 shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-sm flex items-center gap-2">
          <Target className="h-4 w-4 text-primary" />
          {editingGoal ? 'Edit Goal' : 'New Goal'}
        </h4>
        <div className="flex items-center gap-2">
          <Badge
            variant="outline"
            className={`text-xs ${
              totalWeight > remainingBudget
                ? 'border-red-300 text-red-600 bg-red-50'
                : totalWeight === remainingBudget && totalWeight > 0
                ? 'border-emerald-300 text-emerald-700 bg-emerald-50'
                : ''
            }`}
          >
            Weight: {totalWeight}% {remainingBudget < 100 ? `(${remainingBudget}% remaining)` : '/ 100%'}
          </Badge>
          <div className="flex items-center gap-1.5">
            <Switch
              id="goal-active-switch"
              checked={isActive}
              onCheckedChange={setIsActive}
            />
            <Label htmlFor="goal-active-switch" className="text-xs cursor-pointer">
              Active
            </Label>
          </div>
        </div>
      </div>

      {/* Title */}
      <div>
        <Label className="text-xs text-muted-foreground mb-1 block">Goal Title</Label>
        <Input
          placeholder="e.g. Increase Revenue by 15%"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={!!editingGoal}
        />
      </div>

      {/* KPI Table */}
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">KPIs</Label>

        {/* Header row */}
        <div className="grid grid-cols-[1fr_1fr_1fr_80px_32px] gap-2 px-1">
          <span className="text-xs font-medium text-muted-foreground">KPI Name</span>
          <span className="text-xs font-medium text-muted-foreground">Measurement</span>
          <span className="text-xs font-medium text-muted-foreground">Timeline</span>
          <span className="text-xs font-medium text-muted-foreground text-center">Weight %</span>
          <span />
        </div>

        {kpis.map((kpi, idx) => (
          <div
            key={idx}
            className="grid grid-cols-[1fr_1fr_1fr_80px_32px] gap-2 items-center"
          >
            <Input
              placeholder="KPI name"
              value={kpi.name}
              onChange={(e) => updateKpi(idx, 'name', e.target.value)}
              className="h-8 text-sm"
            />
            <Input
              placeholder="Measurement"
              value={kpi.measurement}
              onChange={(e) => updateKpi(idx, 'measurement', e.target.value)}
              className="h-8 text-sm"
            />
            <Input
              placeholder="e.g. Q4 2026"
              value={kpi.timeline}
              onChange={(e) => updateKpi(idx, 'timeline', e.target.value)}
              className="h-8 text-sm"
            />
            <Input
              type="number"
              placeholder="0"
              min="0"
              max="100"
              value={kpi.weight}
              onChange={(e) => updateKpi(idx, 'weight', e.target.value)}
              className="h-8 text-sm text-center"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => removeKpi(idx)}
              disabled={kpis.length <= 1}
              className="h-8 w-8 p-0 text-destructive"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}

        <Button
          variant="outline"
          size="sm"
          onClick={addKpi}
          className="w-full border-dashed text-xs"
        >
          <Plus className="h-3 w-3 mr-1" />
          Add KPI
        </Button>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-1 border-t">
        <Button variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" onClick={handleSave} disabled={saving}>
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin mr-1" />
          ) : (
            <Save className="h-4 w-4 mr-1" />
          )}
          Save Goal
        </Button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// LibraryGoalCard — displays a single goal in template detail view
// ---------------------------------------------------------------------------
const LibraryGoalCard = ({ goal, onEdit, onDelete }) => {
  const kpiCount = (goal.kpis || []).length;
  const totalWeight = (goal.kpis || []).reduce(
    (sum, k) => sum + (parseFloat(k.weight) || 0),
    0
  );

  return (
    <div className="p-3 rounded-lg border bg-white hover:border-primary/40 transition-colors">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Target className="h-4 w-4 text-primary flex-shrink-0" />
          <span className="font-medium text-sm truncate">{goal.title}</span>
          {!goal.is_active && (
            <Badge variant="outline" className="text-xs text-slate-500 flex-shrink-0">
              Inactive
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <Badge variant="outline" className="text-xs">
            Weight: {totalWeight}%
          </Badge>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(goal)}
            className="h-7 w-7 p-0"
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(goal.id)}
            className="h-7 w-7 p-0 text-destructive"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* KPI count */}
      <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <span className="font-medium text-slate-600">KPIs:</span>
          {kpiCount} {kpiCount === 1 ? 'KPI' : 'KPIs'}
        </span>
      </div>

      {/* KPI mini-table */}
      {(goal.kpis || []).length > 0 && (
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-slate-50">
                <th className="text-left p-1.5 font-medium text-muted-foreground border-b">KPI</th>
                <th className="text-left p-1.5 font-medium text-muted-foreground border-b">Measurement</th>
                <th className="text-left p-1.5 font-medium text-muted-foreground border-b">Timeline</th>
                <th className="text-right p-1.5 font-medium text-muted-foreground border-b">Weight</th>
              </tr>
            </thead>
            <tbody>
              {(goal.kpis || []).map((kpi, i) => (
                <tr key={kpi.id || i} className="border-b border-slate-100 last:border-0">
                  <td className="p-1.5 text-slate-700">{kpi.name}</td>
                  <td className="p-1.5 text-slate-600">{kpi.measurement}</td>
                  <td className="p-1.5 text-slate-600">{kpi.timeline || '-'}</td>
                  <td className="p-1.5 text-right font-medium text-slate-700">{kpi.weight}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------
export default function GoalLibraryPage() {
  // Templates list view state
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [showTemplateCreateForm, setShowTemplateCreateForm] = useState(false);

  // Template detail view state
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [goals, setGoals] = useState([]);

  const [loadingSections, setLoadingSections] = useState(false);
  const [loadingGoals, setLoadingGoals] = useState(false);

  // Goal form state
  const [showGoalForm, setShowGoalForm] = useState(null); // null | 'new' | goalId
  const [editingGoal, setEditingGoal] = useState(null);
  const [templateTotalWeight, setTemplateTotalWeight] = useState(0);

  // ---------------------------------------------------------------------------
  // Loaders
  // ---------------------------------------------------------------------------
  const loadTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const res = await goalLibraryTemplatesApi.getAll();
      setTemplates(res.data || []);
    } catch (err) {
      toast.error('Failed to load templates');
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  const loadSections = useCallback(async (templateId) => {
    setLoadingSections(true);
    try {
      const res = await goalLibrarySectionsApi.getAll(templateId);
      setSections(res.data || []);
    } catch (err) {
      toast.error('Failed to load sections');
    } finally {
      setLoadingSections(false);
    }
  }, []);

  const loadGoals = useCallback(async (sectionId) => {
    setLoadingGoals(true);
    try {
      const res = await goalLibraryGoalsApi.getAll(sectionId);
      setGoals(res.data || []);
    } catch (err) {
      toast.error('Failed to load goals');
    } finally {
      setLoadingGoals(false);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------------
  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const refreshTemplateWeight = useCallback(async () => {
    if (!selectedTemplate) return;
    try {
      const res = await goalLibraryTemplatesApi.getById(selectedTemplate.id);
      setTemplateTotalWeight(res.data.total_weight ?? 0);
    } catch (_) {}
  }, [selectedTemplate]);

  useEffect(() => {
    if (selectedTemplate) {
      loadSections(selectedTemplate.id);
      setSelectedSection(null);
      setGoals([]);
      setTemplateTotalWeight(selectedTemplate.total_weight ?? 0);
    }
  }, [selectedTemplate, loadSections]);

  useEffect(() => {
    if (selectedSection) {
      loadGoals(selectedSection.id);
      setShowGoalForm(null);
      setEditingGoal(null);
    } else {
      setGoals([]);
    }
  }, [selectedSection, loadGoals]);

  // ---------------------------------------------------------------------------
  // Handlers — Templates list
  // ---------------------------------------------------------------------------
  const handleCreateTemplate = async (data) => {
    try {
      await goalLibraryTemplatesApi.create(data);
      toast.success('Template created');
      setShowTemplateCreateForm(false);
      loadTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create template');
      throw err;
    }
  };

  const handleDeleteTemplate = async (template) => {
    if ((template.assigned_user_count ?? 0) > 0) {
      toast.error(`Cannot delete: ${template.assigned_user_count} user(s) are assigned to this template`);
      return;
    }
    if (!window.confirm(`Delete template "${template.title}"?`)) return;
    try {
      await goalLibraryTemplatesApi.delete(template.id);
      setTemplates((prev) => prev.filter((t) => t.id !== template.id));
      toast.success('Template deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete template');
    }
  };

  const handleToggleTemplateActive = async (id, isActive) => {
    try {
      await goalLibraryTemplatesApi.update(id, { is_active: isActive });
      setTemplates((prev) =>
        prev.map((t) => (t.id === id ? { ...t, is_active: isActive } : t))
      );
      toast.success(isActive ? 'Template activated' : 'Template deactivated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update template');
    }
  };

  // ---------------------------------------------------------------------------
  // Handlers — Template detail (goals)
  // ---------------------------------------------------------------------------
  const handleSectionsChange = () => {
    if (selectedTemplate) loadSections(selectedTemplate.id);
  };

  const openNewGoalForm = () => {
    setEditingGoal(null);
    setShowGoalForm('new');
  };

  const openEditGoalForm = (goal) => {
    setEditingGoal(goal);
    setShowGoalForm(goal.id);
  };

  const closeGoalForm = () => {
    setShowGoalForm(null);
    setEditingGoal(null);
  };

  const handleSaveGoal = async (data) => {
    if (!selectedSection) return;
    try {
      if (editingGoal) {
        await goalLibraryGoalsApi.update(editingGoal.id, {
          kpis: data.kpis,
          is_active: data.is_active,
        });
        toast.success('Goal updated');
      } else {
        await goalLibraryGoalsApi.create(selectedSection.id, {
          title: data.title,
          kpis: data.kpis,
          is_active: data.is_active,
        });
        toast.success('Goal saved');
      }
      closeGoalForm();
      loadGoals(selectedSection.id);
      refreshTemplateWeight();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save goal');
      throw err;
    }
  };

  const handleDeleteGoal = async (id) => {
    if (!window.confirm('Delete this goal?')) return;
    try {
      await goalLibraryGoalsApi.delete(id);
      setGoals((prev) => prev.filter((g) => g.id !== id));
      toast.success('Goal deleted');
      refreshTemplateWeight();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete goal');
    }
  };

  // ---------------------------------------------------------------------------
  // Render — Templates List View
  // ---------------------------------------------------------------------------
  const renderTemplatesList = () => (
    <div className="flex flex-col h-full space-y-0 animate-fade-in" data-testid="goal-library-page">
      {/* Page header */}
      <div className="px-6 pt-5 pb-4 border-b bg-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary text-primary-foreground flex items-center justify-center">
              <BookOpen className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-2xl font-bold leading-tight">Goal Library</h1>
              <p className="text-sm text-muted-foreground">
                Manage pre-approved goal templates for your organisation
              </p>
            </div>
          </div>
          <Button
            size="sm"
            onClick={() => setShowTemplateCreateForm(true)}
            disabled={showTemplateCreateForm}
          >
            <Plus className="h-4 w-4 mr-1" />
            New Template
          </Button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-6 space-y-3 bg-slate-50/50">
        {loadingTemplates && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        )}

        {showTemplateCreateForm && (
          <InlineTemplateCreateForm
            onSave={handleCreateTemplate}
            onCancel={() => setShowTemplateCreateForm(false)}
          />
        )}

        {!loadingTemplates && templates.length === 0 && !showTemplateCreateForm && (
          <div className="flex flex-col items-center justify-center py-20 text-center text-muted-foreground">
            <BookOpen className="h-10 w-10 mb-3 text-slate-300" />
            <p className="font-medium text-slate-500">
              No templates yet. Click{' '}
              <button
                onClick={() => setShowTemplateCreateForm(true)}
                className="text-primary underline underline-offset-2 hover:no-underline"
              >
                + New Template
              </button>{' '}
              to create one.
            </p>
          </div>
        )}

        {!loadingTemplates &&
          templates.map((template) => (
            <TemplateListCard
              key={template.id}
              template={template}
              onClick={() => setSelectedTemplate(template)}
              onDelete={handleDeleteTemplate}
              onToggleActive={handleToggleTemplateActive}
            />
          ))}
      </div>
    </div>
  );

  // ---------------------------------------------------------------------------
  // Render — Template Detail View
  // ---------------------------------------------------------------------------
  const renderTemplateDetail = () => (
    <div className="flex flex-col h-full space-y-0 animate-fade-in" data-testid="goal-library-page">
      {/* Page header with breadcrumb */}
      <div className="px-6 pt-5 pb-4 border-b bg-white">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedTemplate(null)}
            className="gap-1 text-sm"
          >
            <ChevronLeft className="h-4 w-4" />
            All Templates
          </Button>
          <span className="text-slate-400">/</span>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary text-primary-foreground flex items-center justify-center">
              <BookOpen className="h-4 w-4" />
            </div>
            <h1 className="text-xl font-bold leading-tight">{selectedTemplate.title}</h1>
            <Badge
              variant={selectedTemplate.is_active ? 'default' : 'outline'}
              className={`text-xs ${selectedTemplate.is_active ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'text-slate-500'}`}
            >
              {selectedTemplate.is_active ? 'Active' : 'Inactive'}
            </Badge>
            <Badge
              variant="outline"
              className={`text-xs ${
                templateTotalWeight > 100
                  ? 'border-red-300 text-red-600 bg-red-50'
                  : templateTotalWeight === 100
                  ? 'border-emerald-300 text-emerald-700 bg-emerald-50'
                  : 'text-slate-500'
              }`}
            >
              Total: {templateTotalWeight}% / 100%
            </Badge>
          </div>
        </div>
      </div>

      {/* Two-panel layout */}
      <div className="flex flex-1 min-h-0 overflow-hidden" style={{ height: 'calc(100vh - 140px)' }}>
        {/* Left: Sections panel */}
        <div className="w-64 flex-shrink-0 flex flex-col overflow-hidden">
          {loadingSections ? (
            <div className="flex items-center justify-center h-full border-r bg-slate-50">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : (
            <SectionsPanel
              sections={sections}
              selectedSection={selectedSection}
              onSelectSection={setSelectedSection}
              onSectionsChange={handleSectionsChange}
              templateId={selectedTemplate.id}
            />
          )}
        </div>

        {/* Right: Goals panel */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Right header */}
          <div className="flex items-center justify-between px-5 py-3 border-b bg-white flex-shrink-0">
            <div>
              {selectedSection ? (
                <div className="flex items-center gap-2">
                  <Layers className="h-4 w-4 text-primary" />
                  <span className="font-semibold text-slate-800">{selectedSection.name}</span>
                  <Badge variant="secondary" className="text-xs">
                    {goals.length} goal{goals.length !== 1 ? 's' : ''}
                  </Badge>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Target className="h-4 w-4 text-primary" />
                  <span className="font-semibold text-slate-800">Goals</span>
                </div>
              )}
            </div>
            <Button
              size="sm"
              onClick={openNewGoalForm}
              disabled={!selectedSection || showGoalForm !== null}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Goal
            </Button>
          </div>

          {/* Right body — scrollable */}
          <div className="flex-1 overflow-y-auto p-5 space-y-3 bg-slate-50/50">
            {/* No section selected */}
            {!selectedSection && (
              <div className="flex flex-col items-center justify-center py-20 text-center text-muted-foreground">
                <Layers className="h-10 w-10 mb-3 text-slate-300" />
                <p className="font-medium text-slate-500">
                  Select a section on the left to view goals.
                </p>
              </div>
            )}

            {/* Loading goals */}
            {selectedSection && loadingGoals && (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            )}

            {/* New goal inline form */}
            {selectedSection && showGoalForm === 'new' && (
              <InlineLibraryGoalForm
                editingGoal={null}
                onSave={handleSaveGoal}
                onCancel={closeGoalForm}
                remainingBudget={Math.max(0, 100 - templateTotalWeight)}
              />
            )}

            {/* No goals */}
            {selectedSection && !loadingGoals && goals.length === 0 && showGoalForm !== 'new' && (
              <div className="flex flex-col items-center justify-center py-20 text-center text-muted-foreground">
                <Target className="h-10 w-10 mb-3 text-slate-300" />
                <p className="font-medium text-slate-500">
                  No goals in this section yet. Click{' '}
                  <button
                    onClick={openNewGoalForm}
                    className="text-primary underline underline-offset-2 hover:no-underline"
                  >
                    + Add Goal
                  </button>{' '}
                  to create one.
                </p>
              </div>
            )}

            {/* Goal cards */}
            {selectedSection && !loadingGoals &&
              goals.map((goal) => (
                <div key={goal.id}>
                  {showGoalForm === goal.id ? (
                    <InlineLibraryGoalForm
                      editingGoal={goal}
                      onSave={handleSaveGoal}
                      onCancel={closeGoalForm}
                      remainingBudget={Math.max(0, 100 - templateTotalWeight + (goal.weight || 0))}
                    />
                  ) : (
                    <LibraryGoalCard
                      goal={goal}
                      onEdit={openEditGoalForm}
                      onDelete={handleDeleteGoal}
                    />
                  )}
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );

  // ---------------------------------------------------------------------------
  // Root render
  // ---------------------------------------------------------------------------
  return selectedTemplate === null ? renderTemplatesList() : renderTemplateDetail();
}
